import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/credit-risk`;

interface Dashboard { total_scored: number; distribution: Record<string, number>; total_credit_limit: number; at_risk_credit_limit: number; avg_score: number; }
interface Bucket { bucket: string; balance: number; count: number; loss_rate_pct: number; allowance: number }
interface Pool { pool_key: string; entity_code: string | null; total_ar: number; total_allowance: number; allowance_pct: number; buckets: Bucket[] }
interface Allowance { total_ar: number; total_allowance: number; allowance_pct: number; buckets: Bucket[]; pools?: Pool[] }
interface Candidate { id: number; invoice_number: string; customer_id: string; customer_name: string; balance_due: number; due_date: string; days_overdue: number; needs_cfo_approval: boolean; }
interface Factor { score: number; detail: string; }
interface ScoreResult { customer_id: string; customer_name: string; composite_score: number; previous_score: number | null; rating: string; credit_status: string; dunning_level: number; alert: string | null; factors: Record<string, Factor>; }
interface CustomerRow { customer_id: string; customer_name: string; entity_code: string | null; credit_limit: number; credit_status: string; dunning_level: number; risk_score: number; rating: string }
interface ExposureBreakdown { category_100_open_so: number; category_200_open_ar: number; category_300_special_gl: number; category_400_open_delivery: number; category_500_open_billing: number }
interface CheckStep { check: string; passed: boolean; detail: string }
interface CreditCheck { credit_limit: number; credit_status: string; status_blocked: boolean; total_exposure: number; headroom: number; exposure_breakdown: ExposureBreakdown; checks: CheckStep[]; passed: boolean; message: string }
interface CustomerDetail { customer_id: string; customer_name: string; entity_code: string | null; credit_limit: number; credit_status: string; credit_status_set_at: string | null; credit_status_reason: string | null; credit_review_date: string | null; dunning_level: number; risk_score: number; payment_terms: string | null; currency: string | null; country: string | null; lifecycle_status: string; score_result: ScoreResult | null; credit_check: CreditCheck | null }
interface BlockedOrder { id: number; so_number: string; customer_id: string; customer_name: string; entity_code: string | null; total_amount: number; currency: string | null; lifecycle_status: string; credit_check_message: string | null; created_at: string | null }
interface CreditDecision { id: number; context_type: string; context_id: number; action_label: string; amount: number; entity_code: string | null; status: string; created_at: string | null }

const RATING_COLORS: Record<string, string> = { LOW: 'var(--positive)', MODERATE: 'var(--accent)', ELEVATED: 'var(--warning)', HIGH: '#f97316', CRITICAL: 'var(--negative)', UNSCORED: 'var(--text-hint)' };
const STATUS_COLORS: Record<string, string> = { OK: 'var(--positive)', WATCH: 'var(--warning)', CREDIT_HOLD: '#f97316', BLOCKED: 'var(--negative)' };

export default function CreditRisk() {
  const { entity } = useGlobalEntity();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [allowance, setAllowance] = useState<Allowance | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [blockedOrders, setBlockedOrders] = useState<BlockedOrder[]>([]);
  const [decisions, setDecisions] = useState<CreditDecision[]>([]);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusModalCustomer, setStatusModalCustomer] = useState<string | null>(null);
  const [statusModalNew, setStatusModalNew] = useState<string>('OK');
  const [statusModalReason, setStatusModalReason] = useState<string>('');
  const [ratingFilter, setRatingFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<string>('risk');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const customersUrl = ratingFilter
        ? `${API}/customers?rating=${ratingFilter}`
        : `${API}/customers`;
      const [dashRes, allowRes, candRes, custRes, blkRes, decRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/dashboard`, entity)),
        authFetch(withEntityParam(`${API}/allowance`, entity)),
        authFetch(withEntityParam(`${API}/write-off-candidates`, entity)),
        authFetch(withEntityParam(customersUrl, entity)),
        authFetch(withEntityParam(`${API}/blocked-orders`, entity)),
        authFetch(withEntityParam(`${API}/credit-decisions`, entity)),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (allowRes.ok) setAllowance(await allowRes.json());
      if (candRes.ok) { const d = await candRes.json(); setCandidates(d.candidates || []); }
      if (custRes.ok) { const d = await custRes.json(); setCustomers(d.customers || []); }
      if (blkRes.ok) { const d = await blkRes.json(); setBlockedOrders(d.orders || []); }
      if (decRes.ok) { const d = await decRes.json(); setDecisions(d.decisions || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity, ratingFilter]);

  useEffect(() => { load(); }, [load]);

  const handleScoreAll = async () => {
    try {
      const res = await authFetch(`${API}/score-all`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); showToast(`Scored ${d.scored} customers`, true); load(); }
    } catch { showToast('Scoring failed', false); }
  };

  const handlePostProvision = async () => {
    try {
      const res = await authFetch(`${API}/post-allowance-provision`, { method: 'POST' });
      if (res.ok) { const d = await res.json(); showToast(`F107: ${d.posted} posted, ${d.skipped} already done, ${d.no_op} no-op`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Failed', false); }
    } catch { showToast('Failed', false); }
  };

  const openCustomerDetail = async (customerId: string) => {
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await authFetch(`${API}/customer/${encodeURIComponent(customerId)}/detail`);
      if (res.ok) setDetail(await res.json());
      else showToast('Failed to load customer detail', false);
    } catch { showToast('Failed to load customer detail', false); }
    setDetailLoading(false);
  };

  const closeDetail = () => { setDetail(null); setDetailLoading(false); };

  const openStatusModal = (customerId: string, currentStatus: string) => {
    setStatusModalCustomer(customerId);
    setStatusModalNew(currentStatus === 'CREDIT_HOLD' || currentStatus === 'BLOCKED' ? 'OK' : 'CREDIT_HOLD');
    setStatusModalReason('');
  };

  const submitStatusChange = async () => {
    if (!statusModalCustomer) return;
    try {
      const res = await authFetch(`${API}/customer/${encodeURIComponent(statusModalCustomer)}/credit-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credit_status: statusModalNew, reason: statusModalReason || null }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Status: ${d.prior_status} -> ${d.new_status}`, true);
        setStatusModalCustomer(null);
        if (detail && detail.customer_id === statusModalCustomer) openCustomerDetail(statusModalCustomer);
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Failed', false);
      }
    } catch { showToast('Failed', false); }
  };

  const handleWriteOff = async (id: number, method: string) => {
    try {
      const res = await authFetch(`${API}/write-off/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ method, reason: 'Aged beyond collection threshold' }) });
      if (res.ok) { const d = await res.json(); showToast(d.status === 'PENDING_APPROVAL' ? 'Sent for CFO approval' : 'Written off', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const drillToRating = (rating: string) => {
    setRatingFilter(rating);
    setTab('customers');
  };

  const allowanceColumns: Column[] = [
    { key: 'bucket', label: 'Aging Bucket', render: (v: string) => <span style={{ fontWeight: 600 }}>{v.replace('_', '-')}</span> },
    { key: 'count', label: 'Invoices' },
    { key: 'balance', label: 'Balance', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'loss_rate_pct', label: 'Loss Rate', align: 'right', render: (v: number) => <span style={{ color: 'var(--text-secondary)' }}>{v}%</span> },
    { key: 'allowance', label: 'Allowance', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: 'var(--negative)' }}>{formatAmount(v)}</span> },
  ];

  const customerColumns: Column[] = [
    { key: 'customer_id', label: 'ID', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer' },
    { key: 'entity_code', label: 'Entity', render: (v: string | null) => <span style={{ color: 'var(--text-secondary)' }}>{v || '—'}</span> },
    { key: 'credit_limit', label: 'Limit', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'credit_status', label: 'Status', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLORS[v] || 'var(--text-hint)' }}>{v}</span> },
    { key: 'dunning_level', label: 'Dun', align: 'right', render: (v: number) => v > 0 ? <span style={{ fontWeight: 600, color: v >= 4 ? 'var(--negative)' : 'var(--warning)' }}>{v}/5</span> : <span style={{ color: 'var(--text-hint)' }}>—</span> },
    { key: 'risk_score', label: 'Score', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{v.toFixed(1)}</span> },
    { key: 'rating', label: 'Rating', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600, color: RATING_COLORS[v] || 'var(--text-hint)' }}>{v}</span> },
  ];

  const writeoffColumns: Column[] = [
    { key: 'invoice_number', label: 'Invoice', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: string, row: Candidate) => v || row.customer_id },
    { key: 'balance_due', label: 'Balance', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'due_date', label: 'Due Date', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
    { key: 'days_overdue', label: 'Days Overdue', render: (v: number) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'actions', label: 'Actions', render: (_: any, c: Candidate) => (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <ActionButton label="Allowance" onClick={(e) => { e.stopPropagation(); handleWriteOff(c.id, 'ALLOWANCE'); }} variant="secondary" size="sm" />
        <ActionButton label="Write Off" onClick={(e) => { e.stopPropagation(); handleWriteOff(c.id, 'DIRECT'); }} variant="danger" size="sm" />
        {c.needs_cfo_approval && <span style={{ fontSize: 9, color: 'var(--warning)', fontWeight: 600 }}>CFO req.</span>}
      </div>
    )},
  ];

  const blockedColumns: Column[] = [
    { key: 'so_number', label: 'SO #', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: string, row: BlockedOrder) => v || row.customer_id },
    { key: 'entity_code', label: 'Entity', render: (v: string | null) => <span style={{ color: 'var(--text-secondary)' }}>{v || '—'}</span> },
    { key: 'total_amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'lifecycle_status', label: 'Status', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600 }}>{v}</span> },
    { key: 'credit_check_message', label: 'Reason', render: (v: string | null) => <span style={{ fontSize: 11, color: 'var(--negative)' }}>{v || '—'}</span> },
  ];

  const decisionColumns: Column[] = [
    { key: 'context_type', label: 'Type', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'action_label', label: 'Subject' },
    { key: 'entity_code', label: 'Entity', render: (v: string | null) => <span style={{ color: 'var(--text-secondary)' }}>{v || '—'}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'status', label: 'Status', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600 }}>{v}</span> },
    { key: 'created_at', label: 'Created', render: (v: string | null) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v ? v.slice(0, 10) : '—'}</span> },
  ];

  return (
    <PageLayout
      title="Credit Risk & Write-Off"
      subtitle="5-factor credit monitoring, allowance recommendation, and write-off management"
      action={{ label: 'Score All Customers', onClick: handleScoreAll }}
    >
      <TabBar
        tabs={[
          { key: 'risk', label: 'Risk Dashboard' },
          { key: 'customers', label: `Customers${ratingFilter ? ` (${ratingFilter})` : ''}` },
          { key: 'allowance', label: 'Allowance' },
          { key: 'writeoff', label: 'Write-Off Candidates' },
          { key: 'blocked', label: `Blocked Orders${blockedOrders.length ? ` (${blockedOrders.length})` : ''}` },
          { key: 'decisions', label: `Credit Decisions${decisions.length ? ` (${decisions.length})` : ''}` },
        ]}
        active={tab}
        onChange={setTab}
      />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : tab === 'risk' ? (
        dashboard && (
          <div>
            <KpiGrid items={[
              { label: 'Customers Scored', value: dashboard.total_scored, color: 'var(--text-primary)' },
              { label: 'Avg Score', value: dashboard.avg_score, color: 'var(--accent)' },
              { label: 'Total Credit Limit', value: formatAmountCompact(dashboard.total_credit_limit), color: 'var(--text-primary)' },
              { label: 'At-Risk Credit', value: formatAmountCompact(dashboard.at_risk_credit_limit), color: 'var(--negative)' },
            ]} />
            <div style={{ display: 'flex', gap: 12 }}>
              {Object.entries(dashboard.distribution).map(([rating, count]) => (
                <div
                  key={rating}
                  role="button"
                  tabIndex={0}
                  onClick={() => drillToRating(rating)}
                  onKeyDown={(e) => { if (e.key === 'Enter') drillToRating(rating); }}
                  title={`Click to view ${rating} customers`}
                  style={{ flex: 1, padding: '16px 20px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${RATING_COLORS[rating] || 'var(--border-subtle)'}`, cursor: 'pointer' }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{rating}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: RATING_COLORS[rating] }}>{String(count)}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-hint)' }}>Click any rating to drill through. Risk scores recompute nightly (Phase 14).</div>
          </div>
        )
      ) : tab === 'customers' ? (
        <div>
          {ratingFilter && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Filtered by rating:</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: RATING_COLORS[ratingFilter] || 'var(--text-hint)' }}>{ratingFilter}</span>
              <ActionButton label="Clear" onClick={() => setRatingFilter(null)} variant="secondary" size="sm" />
            </div>
          )}
          <DataTable
            columns={customerColumns}
            rows={customers}
            rowKey="customer_id"
            onRowClick={(c: CustomerRow) => openCustomerDetail(c.customer_id)}
            emptyMessage="No scored customers yet. Click Score All Customers."
          />
        </div>
      ) : tab === 'allowance' ? (
        allowance && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <ActionButton label="Post Period-End Provision (F107)" onClick={handlePostProvision} variant="primary" size="sm" />
            </div>
            <KpiGrid items={[
              { label: 'Total AR', value: formatAmountCompact(allowance.total_ar) },
              { label: 'Recommended Allowance', value: formatAmountCompact(allowance.total_allowance), color: 'var(--negative)' },
              { label: 'Allowance %', value: `${allowance.allowance_pct.toFixed(1)}%`, color: 'var(--warning)' },
            ]} />
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 16, marginBottom: 6 }}>Portfolio (org-wide)</div>
            <DataTable
              columns={allowanceColumns}
              rows={allowance.buckets}
              rowKey="bucket"
            />
            {allowance.pools && allowance.pools.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Pools (entity-segmented — IFRS 9 / ASC 326)</div>
                {allowance.pools.map(pool => (
                  <div key={pool.pool_key} style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 16, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Entity {pool.entity_code || 'UNASSIGNED'}
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>AR <strong style={{ color: 'var(--text-primary)' }}>{formatAmount(pool.total_ar)}</strong></span>
                        <span style={{ color: 'var(--text-secondary)' }}>Allowance <strong style={{ color: 'var(--negative)' }}>{formatAmount(pool.total_allowance)}</strong></span>
                        <span style={{ color: 'var(--text-secondary)' }}>Rate <strong style={{ color: 'var(--warning)' }}>{pool.allowance_pct.toFixed(2)}%</strong></span>
                      </div>
                    </div>
                    <DataTable
                      columns={allowanceColumns}
                      rows={pool.buckets}
                      rowKey="bucket"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      ) : tab === 'writeoff' ? (
        <DataTable
          columns={writeoffColumns}
          rows={candidates}
          emptyMessage="No write-off candidates (90+ days overdue)."
          rowKey="id"
        />
      ) : tab === 'blocked' ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>Sales orders failing credit check at confirm time. SAP VKM1 equivalent.</div>
          <DataTable
            columns={blockedColumns}
            rows={blockedOrders}
            rowKey="id"
            onRowClick={(o: BlockedOrder) => openCustomerDetail(o.customer_id)}
            emptyMessage="No blocked sales orders."
          />
        </div>
      ) : tab === 'decisions' ? (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>Open credit-decision actions: SO credit reviews + write-off approvals + write-off reviews. SAP UKM_MY_DCDS equivalent.</div>
          <DataTable
            columns={decisionColumns}
            rows={decisions}
            rowKey="id"
            emptyMessage="No open credit decisions."
          />
        </div>
      ) : null}

      {/* Customer Credit Master Sheet modal (SAP UKM_BP_DISPLAY) */}
      {(detail || detailLoading) && (
        <div
          role="presentation"
          onClick={closeDetail}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-subtle)', padding: 24, maxWidth: 900, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
          >
            {detailLoading || !detail ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading customer detail...</div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{detail.customer_name || detail.customer_id}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {detail.customer_id} · {detail.entity_code || 'no entity'} · {detail.currency || 'USD'} · {detail.country || '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ActionButton label="Change Credit Status" onClick={() => openStatusModal(detail.customer_id, detail.credit_status)} variant="secondary" size="sm" />
                    <ActionButton label="Close" onClick={closeDetail} variant="secondary" size="sm" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
                  <div style={{ padding: 12, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Limit</div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{formatAmount(detail.credit_limit)}</div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: STATUS_COLORS[detail.credit_status] || 'var(--text-hint)' }}>{detail.credit_status}</div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dunning</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: detail.dunning_level >= 4 ? 'var(--negative)' : detail.dunning_level >= 2 ? 'var(--warning)' : 'var(--text-hint)' }}>{detail.dunning_level}/5</div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Risk Score</div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4, color: RATING_COLORS[detail.score_result?.rating || 'UNSCORED'] }}>
                      {detail.risk_score.toFixed(1)} <span style={{ fontSize: 10, fontWeight: 600 }}>{detail.score_result?.rating}</span>
                    </div>
                  </div>
                </div>

                {detail.credit_status_reason && (
                  <div style={{ padding: 10, background: 'var(--warning-bg, rgba(245,158,11,0.1))', borderLeft: '3px solid var(--warning)', borderRadius: 4, marginBottom: 16, fontSize: 12, color: 'var(--text-primary)' }}>
                    <strong>Status reason:</strong> {detail.credit_status_reason}
                    {detail.credit_status_set_at && <span style={{ color: 'var(--text-hint)' }}> ({detail.credit_status_set_at.slice(0, 10)})</span>}
                  </div>
                )}

                {detail.credit_check && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Exposure (SAP Liability Categories)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, fontSize: 11 }}>
                      <div style={{ padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 4 }}>
                        <div style={{ color: 'var(--text-hint)' }}>100 Open SO</div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(detail.credit_check.exposure_breakdown.category_100_open_so)}</div>
                      </div>
                      <div style={{ padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 4 }}>
                        <div style={{ color: 'var(--text-hint)' }}>200 Open AR</div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(detail.credit_check.exposure_breakdown.category_200_open_ar)}</div>
                      </div>
                      <div style={{ padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 4 }}>
                        <div style={{ color: 'var(--text-hint)' }}>300 Special G/L</div>
                        <div style={{ fontWeight: 600, color: detail.credit_check.exposure_breakdown.category_300_special_gl < 0 ? 'var(--positive)' : 'inherit' }}>{formatAmount(detail.credit_check.exposure_breakdown.category_300_special_gl)}</div>
                      </div>
                      <div style={{ padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 4 }}>
                        <div style={{ color: 'var(--text-hint)' }}>400 Deliveries</div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(detail.credit_check.exposure_breakdown.category_400_open_delivery)}</div>
                      </div>
                      <div style={{ padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.02))', borderRadius: 4 }}>
                        <div style={{ color: 'var(--text-hint)' }}>500 Billing</div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(detail.credit_check.exposure_breakdown.category_500_open_billing)}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, padding: '8px 0', borderTop: '1px solid var(--border-subtle)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Total exposure <strong style={{ color: 'var(--text-primary)' }}>{formatAmount(detail.credit_check.total_exposure)}</strong></span>
                      <span style={{ color: 'var(--text-secondary)' }}>Headroom <strong style={{ color: detail.credit_check.headroom < 0 ? 'var(--negative)' : 'var(--positive)' }}>{formatAmount(detail.credit_check.headroom)}</strong></span>
                    </div>
                  </div>
                )}

                {detail.credit_check && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Check-Rule Steps (SAP UKM_CHECK_*)</div>
                    {detail.credit_check.checks.map(c => (
                      <div key={c.check} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12 }}>
                        <span style={{ width: 16, color: c.passed ? 'var(--positive)' : 'var(--negative)' }}>{c.passed ? '✓' : '✗'}</span>
                        <span style={{ minWidth: 160, fontWeight: 600 }}>{c.check}</span>
                        <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{c.detail}</span>
                      </div>
                    ))}
                  </div>
                )}

                {detail.score_result && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>5-Factor Score Breakdown</div>
                    {Object.entries(detail.score_result.factors).map(([key, f]) => (
                      <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12 }}>
                        <span style={{ minWidth: 160, fontWeight: 600 }}>{key.replace(/_/g, ' ')}</span>
                        <span style={{ minWidth: 40, fontWeight: 700, color: f.score >= 80 ? 'var(--positive)' : f.score >= 60 ? 'var(--accent)' : f.score >= 40 ? 'var(--warning)' : 'var(--negative)' }}>{f.score}</span>
                        <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{f.detail}</span>
                      </div>
                    ))}
                    {detail.score_result.alert && (
                      <div style={{ marginTop: 10, padding: 10, background: 'var(--warning-bg, rgba(245,158,11,0.1))', borderLeft: '3px solid var(--warning)', borderRadius: 4, fontSize: 12 }}>
                        <strong>Alert:</strong> {detail.score_result.alert}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Credit-status change modal */}
      {statusModalCustomer && (
        <div
          role="presentation"
          onClick={() => setStatusModalCustomer(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border-subtle)', padding: 24, maxWidth: 480, width: '100%' }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Change credit status</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>Customer: {statusModalCustomer}</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>New status</div>
              <select
                value={statusModalNew}
                onChange={(e) => setStatusModalNew(e.target.value)}
                style={{ width: '100%', padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.04))', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13 }}
              >
                <option value="OK">OK</option>
                <option value="WATCH">WATCH</option>
                <option value="CREDIT_HOLD">CREDIT_HOLD</option>
                <option value="BLOCKED">BLOCKED</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Reason (required for audit trail)</div>
              <textarea
                value={statusModalReason}
                onChange={(e) => setStatusModalReason(e.target.value)}
                rows={3}
                placeholder="e.g. Customer provided payment commitment for outstanding $X; clearing hold."
                style={{ width: '100%', padding: 8, background: 'var(--bg-elevated, rgba(255,255,255,0.04))', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <ActionButton label="Cancel" onClick={() => setStatusModalCustomer(null)} variant="secondary" size="sm" />
              <ActionButton label="Apply" onClick={submitStatusChange} variant="primary" size="sm" />
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 3500, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
